import React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Info, RefreshCw } from "lucide-react";
import { useSubscription, useUsageStats } from "@/hooks/useBillingAndUsage";
import { PlanOverviewCard } from "./PlanOverviewCard";
import { UsageLimitsCard } from "./UsageLimitsCard";

interface OverviewSettingsProps {
  onUpgrade: () => void;
}

export const OverviewSettings: React.FC<OverviewSettingsProps> = ({
  onUpgrade,
}) => {
  const {
    data: subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refetch: refetchSubscription,
  } = useSubscription();
  const {
    data: usage,
    isLoading: usageLoading,
    error: usageError,
    refetch: refetchUsage,
  } = useUsageStats();

  if (subscriptionLoading || usageLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-36 w-full rounded-xl" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  // Network / server query error
  if (subscriptionError || usageError) {
    return (
      <Alert variant="destructive" className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <AlertTitle className="text-base font-semibold">Unable to load subscription details</AlertTitle>
        </div>
        <AlertDescription className="text-sm">
          An error occurred while fetching your organization's subscription and usage information.
        </AlertDescription>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            refetchSubscription();
            refetchUsage();
          }}
          className="w-fit"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </Alert>
    );
  }

  // Subscription not yet provisioned in DB
  if (!subscription) {
    return (
      <Card className="border-dashed border-purple-200 bg-purple-50/50">
        <CardContent className="flex flex-col items-center justify-center p-8 text-center">
          <Info className="h-10 w-10 text-purple-600 mb-3" />
          <h3 className="text-lg font-bold text-gray-900">Subscription Provisioning in Progress</h3>
          <p className="mt-1 max-w-md text-sm text-gray-600">
            Your organization subscription is currently being initialized. If this message persists after a few moments, please refresh the page or contact support.
          </p>
          <Button
            onClick={() => {
              refetchSubscription();
              refetchUsage();
            }}
            variant="outline"
            className="mt-4"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Subscription
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Usage fallback object if usage stats view row is initializing
  const activeUsage = usage || {
    organization_id: subscription.organization_id,
    influencer_count: 0,
    campaign_count: 0,
    team_member_count: 1,
    deliverable_count: 0,
    profile_refresh_count: 0,
  };

  return (
    <div className="space-y-4">
      <PlanOverviewCard
        planName={subscription.plan_name}
        isActive={subscription.is_active}
        status={subscription.status}
        trialEndsAt={subscription.trial_ends_at}
        onUpgrade={onUpgrade}
      />
      <UsageLimitsCard subscription={subscription} usage={activeUsage} />
    </div>
  );
};
